
import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useSnackbar } from "notistack";
import Layout from "../Layout/Layout";
import { APIUrl } from "../../Helper/APIUrl";

function AddTrustedPartner() {
  const Token = JSON.parse(localStorage.getItem("tcn"));
  const localStorageUserId = JSON.parse(localStorage.getItem("userid"));
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState("");
  const [trustedPartners, setTrustedPartners] = useState([]);
  const [filteredTrustedPartners, setFilteredTrustedPartners] = useState([]);

  const [Id, setId] = useState(0);
  const [PartnerName, setPartnerName] = useState("");
  const [AltText, setAltText] = useState("");
  const [Logo, setLogo] = useState("");
  const [ImageFile, setImageFile] = useState(null);
  const [ImagePreview, setImagePreview] = useState("");
  const [IsActive, setIsActive] = useState(true);
  const [edit, setEdit] = useState(false);

  const PartnerNameRef = useRef(null);
  const AltTextRef = useRef(null);
  const ImageRef = useRef(null);

  const parseJSON = async (response) => {
    try {
      const text = await response.text();
      if (!text) return null;
      return JSON.parse(text);
    } catch (err) {
      console.warn("Failed to parse JSON response", err);
      return null;
    }
  };

  const getImageSource = (imagePath) => {
    if (!imagePath) return "";
    if (String(imagePath).startsWith("http")) return imagePath;
    return `${APIUrl.BaseUrl}${imagePath}`;
  };

  const doEmptyStates = () => {
    if (ImagePreview && ImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(ImagePreview);
    }

    setId(0);
    setPartnerName("");
    setAltText("");
    setLogo("");
    setImageFile(null);
    setImagePreview("");
    setIsActive(true);
    setEdit(false);

    if (ImageRef.current) {
      ImageRef.current.value = "";
    }
  };

  const getAllTrustedPartners = async () => {
    try {
      const response = await fetch(APIUrl.TrustedPartner + "GetAllTrustedPartner", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Token}`,
        },
        body: JSON.stringify({}),
      });

      const result = await parseJSON(response);
      if (result && Array.isArray(result.data)) {
        setTrustedPartners(result.data);
        setFilteredTrustedPartners(result.data);
      } else {
        setTrustedPartners([]);
        setFilteredTrustedPartners([]);
      }
    } catch (error) {
      console.log(error);
      setTrustedPartners([]);
      setFilteredTrustedPartners([]);
    }
  };

  useEffect(() => {
    if (!Token) {
      enqueueSnackbar("Please login first", { variant: "warning" });
      return;
    }

    getAllTrustedPartners();
  }, []);

  useEffect(() => {
    const txt = search.toLowerCase();
    const result = trustedPartners.filter((x) => {
      const partnerName = (x.PartnerName || "").toLowerCase();
      const altText = (x.AltText || "").toLowerCase();
      return partnerName.includes(txt) || altText.includes(txt);
    });

    setFilteredTrustedPartners(result);
  }, [search, trustedPartners]);

  useEffect(() => {
    return () => {
      if (ImagePreview && ImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(ImagePreview);
      }
    };
  }, [ImagePreview]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isValidType = ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type);
    if (!isValidType) {
      enqueueSnackbar("Only JPG, JPEG, PNG, or WEBP files are allowed.", { variant: "warning" });
      event.target.value = "";
      return;
    }

    if (ImagePreview && ImagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(ImagePreview);
    }

    const preview = URL.createObjectURL(file);
    setImageFile(file);
    setImagePreview(preview);
  };

  const validateForm = () => {
    if (!PartnerName || PartnerName.trim().length < 2) {
      enqueueSnackbar("Partner Name is required.", { variant: "warning" });
      PartnerNameRef.current?.focus();
      return false;
    }

    if (!AltText || AltText.trim().length < 2) {
      enqueueSnackbar("Alt Text is required.", { variant: "warning" });
      AltTextRef.current?.focus();
      return false;
    }

    if (!edit && !ImageFile) {
      enqueueSnackbar("Please upload logo.", { variant: "warning" });
      ImageRef.current?.focus();
      return false;
    }

    return true;
  };

  const insertTrustedPartner = async () => {
    try {
      if (!validateForm()) return;

      const payload = {
        PartnerName,
        AltText,
        Logo: "",
        IsActive: true,
        CreatedBy: localStorageUserId || 0,
        ModifyBy: localStorageUserId || 0,
      };

      const formData = new FormData();
      formData.append("TrustedPartnerJson", JSON.stringify(payload));

      if (ImageFile) {
        formData.append("Image", ImageFile);
      }

      const response = await fetch(APIUrl.TrustedPartner + "InsertTrustedPartner", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Token}`,
        },
        body: formData,
      });

      const result = await parseJSON(response);

      if (response.ok && result) {
        enqueueSnackbar("Successfully Added", { variant: "success" });
        getAllTrustedPartners();
        doEmptyStates();
      } else {
        enqueueSnackbar(result?.Message || "Failed to add Trusted Partner", { variant: "error" });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error adding Trusted Partner", { variant: "error" });
    }
  };

  const updateTrustedPartner = async (_Id) => {
    try {
      if (!validateForm()) return;

      const payload = {
        Id: _Id,
        PartnerName,
        AltText,
        Logo,
        IsActive,
        CreatedBy: localStorageUserId || 0,
        ModifyBy: localStorageUserId || 0,
      };

      const formData = new FormData();
      formData.append("TrustedPartnerJson", JSON.stringify(payload));

      if (ImageFile) {
        formData.append("Image", ImageFile);
      }

      const response = await fetch(APIUrl.TrustedPartner + "UpdateTrustedPartner/" + _Id, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Token}`,
        },
        body: formData,
      });

      const result = await parseJSON(response);

      if (response.ok && result) {
        enqueueSnackbar("Successfully Updated", { variant: "success" });
        getAllTrustedPartners();
        doEmptyStates();
      } else {
        enqueueSnackbar(result?.Message || "Failed to update Trusted Partner", { variant: "error" });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error updating Trusted Partner", { variant: "error" });
    }
  };

  const editTrustedPartner = (row) => {
    setId(row.Id);
    setPartnerName(row.PartnerName || "");
    setAltText(row.AltText || "");
    setLogo(row.Logo || "");
    setImageFile(null);
    setImagePreview(getImageSource(row.Logo));
    setIsActive(row.IsActive ?? true);
    setEdit(true);

    if (ImageRef.current) {
      ImageRef.current.value = "";
    }

    PartnerNameRef.current?.focus();
  };

  const activateDeactivateTrustedPartner = async (_Id, Value) => {
    try {
      const response = await fetch(APIUrl.TrustedPartner + "ActivateDeactivateTrustedPartner/" + _Id, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Token}`,
        },
        body: JSON.stringify({ Value }),
      });

      const result = await parseJSON(response);

      if (response.ok && result) {
        enqueueSnackbar(Value === "activate" ? "Successfully Activated" : "Successfully Deactivated", {
          variant: Value === "activate" ? "success" : "warning",
        });
        getAllTrustedPartners();
      } else {
        enqueueSnackbar(result?.Message || "Failed to update status", { variant: "error" });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error updating status", { variant: "error" });
    }
  };

  const columns = [
    {
      name: "SNo.",
      selector: (_, index) => index + 1,
      maxWidth: "70px",
    },
    {
      name: "Partner Name",
      selector: (row) => row.PartnerName,
      sortable: true,
      grow: 2,
    },
    {
      name: "Alt Text",
      selector: (row) => row.AltText,
      sortable: true,
      grow: 2,
      wrap: true,
    },
    {
      name: "Logo",
      grow: 2,
      cell: (row) =>
        row.Logo ? (
          <img
            src={getImageSource(row.Logo)}
            alt={row.AltText || row.PartnerName || "Trusted Partner"}
            className="h-16 w-24 object-cover rounded-md border border-gray-200 my-2"
          />
        ) : (
          <span className="text-xs text-gray-500">No image</span>
        ),
    },
    {
      name: "Status",
      cell: (row) =>
        row.IsActive ? (
          <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Activated</label>
        ) : (
          <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Deactivated</label>
        ),
      grow: 1,
    },
    {
      name: "Action",
      grow: 1,
      cell: (row) => (
        <div className="flex items-center gap-2 py-2">
          <Button
            onClick={() => editTrustedPartner(row)}
            className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2"
            title="Edit Trusted Partner"
          >
            <i className="fa fa-pencil" aria-hidden="true"></i>
          </Button>

          {row.IsActive ? (
            <Button
              onClick={() => activateDeactivateTrustedPartner(row.Id, "deactivate")}
              className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-2"
              title="Deactivate Trusted Partner"
            >
              <i className="fa fa-toggle-on" aria-hidden="true"></i>
            </Button>
          ) : (
            <Button
              onClick={() => activateDeactivateTrustedPartner(row.Id, "activate")}
              className="text-white bg-gradient-to-r from-red-400 via-red-500 to-red-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-3 py-2"
              title="Activate Trusted Partner"
            >
              <i className="fa fa-toggle-off" aria-hidden="true"></i>
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Manage Trusted Partner</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Partner Name</label>
                <input
                  ref={PartnerNameRef}
                  type="text"
                  value={PartnerName}
                  onChange={(e) => setPartnerName(e.target.value)}
                  placeholder="Enter Partner Name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
                <input
                  ref={AltTextRef}
                  type="text"
                  value={AltText}
                  onChange={(e) => setAltText(e.target.value)}
                  placeholder="Enter Alt Text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Logo Upload</label>
                <input
                  ref={ImageRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            {ImagePreview && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Logo Preview</p>
                <img
                  src={ImagePreview}
                  alt="Preview"
                  className="h-28 w-44 object-cover rounded-md border border-gray-200"
                />
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              {!edit ? (
                <Button
                  onClick={insertTrustedPartner}
                  className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Add
                </Button>
              ) : (
                <Button
                  onClick={() => updateTrustedPartner(Id)}
                  className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Update
                </Button>
              )}

              <button
                type="button"
                onClick={doEmptyStates}
                className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <DataTable
                title="Get All Trusted Partner"
                columns={columns}
                data={filteredTrustedPartners}
                pagination
                fixedHeader
                fixedHeaderScrollHeight="550px"
                highlightOnHover
                subHeader
                subHeaderAlign="right"
                subHeaderComponent={
                  <input
                    type="text"
                    placeholder="Search partner name / alt text"
                    className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                }
              />
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default AddTrustedPartner;
